<template>
  <div class="auth">
    <NavBar />
    <h3>{{ t('register-title') }}</h3>

    <form v-if="!id" novalidate @submit.prevent="register">
      <label>{{ t('field-email') }}<input v-model="email" type="email" name="email" autocomplete="email" /></label>
      <label>{{ t('field-display-name') }}<input v-model="displayName" name="displayName" autocomplete="nickname" /></label>
      <label>{{ t('field-password') }}<input v-model="password" type="password" name="password" autocomplete="new-password" /></label>
      <label>{{ t('field-password-again') }}<input v-model="password2" type="password" name="password2" autocomplete="new-password" /></label>
      <p v-if="error" class="warn">{{ error }}</p>
      <button type="submit" :disabled="busy">{{ t('button-register') }}</button>
    </form>

    <div v-else-if="done">
      <p class="info">{{ t('register-done') }}</p>
      <router-link :to="{ name: 'signin' }">{{ t('home-sign-in-link') }}</router-link>
    </div>

    <div v-else-if="expired">
      <p class="warn">{{ t('register-expired') }}</p>
      <router-link :to="{ name: 'register' }" @click.native="reset">{{ t('home-register-link') }}</router-link>
    </div>

    <form v-else novalidate @submit.prevent="verify">
      <p v-if="email" class="info">{{ t('register-code-sent', { email }) }}</p>
      <label>{{ t('field-code') }}<input v-model="code" name="code" inputmode="numeric" autocomplete="one-time-code" /></label>
      <p v-if="error" class="warn">{{ error }}</p>
      <button type="submit" :disabled="busy">{{ t('button-verify') }}</button>
    </form>
  </div>
</template>

<script>
import NavBar from './NavBar';
import auth from '@/auth';
import { t, errorText } from '@/texts';

export default {
  name: 'RegisterPage',
  components: { NavBar },
  data() {
    return {
      email: '', displayName: '', password: '', password2: '',
      id: this.$route.query.id || null,
      code: this.$route.query.code || '',
      expired: false, done: false, error: null, busy: false, stopWatching: null,
    };
  },
  methods: {
    t,
    // Done elsewhere (e.g. the emailed link opened in another tab or on another device): go on if
    // this browser is signed in, otherwise say so and offer sign-in.
    alreadyDone() {
      if (this.$store.state.user) this.goOn();
      else this.done = true;
    },
    // Registering started from the sign-in page of another page (?next=) goes back to it.
    goOn() {
      this.$router.replace(this.$route.query.next || { name: 'list' });
    },
    reset() {
      Object.assign(this, { id: null, code: '', expired: false, done: false, error: null });
    },
    async run(action) {
      this.error = null;
      this.busy = true;
      try {
        await action();
      } catch (e) {
        const code = e.msg || String(e);
        if (code === 'already-done') this.alreadyDone();
        else if (code === 'expired') this.expired = true;
        else this.error = errorText(code);
      } finally {
        this.busy = false;
      }
    },
    register() {
      if (this.password !== this.password2) { this.error = t('error-passwords-differ'); return; }
      return this.run(async () => {
        this.id = await auth.register(this.email, this.displayName, this.password);
      });
    },
    verify() {
      return this.run(async () => {
        await auth.verifyRegistration(this.id, this.code.trim());
        this.goOn();
      });
    },
  },
  mounted() {
    // While waiting for the code, follow a sign-in in another tab (the emailed link).
    this.stopWatching = auth.watchOtherTabs(() => this.goOn());
  },
  beforeDestroy() {
    if (this.stopWatching) this.stopWatching();
  },
  created() {
    // Opened from the emailed link: the code is filled in; verify right away.
    if (this.id && this.code) this.verify();
  },
};
</script>

<style src="./auth.css"></style>
